/**
 * ApiKeyRevealDialog — one-time secret reveal after minting a gateway key.
 *
 * Shared by Settings and the model page. The secret only exists in the
 * caller's state (the API returns it exactly once); this dialog just shows
 * it with a copy button.
 */

import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import type { ApiKeyCreateResponse } from '@/hooks/useApiKeys'

interface ApiKeyRevealDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  apiKey: ApiKeyCreateResponse | null
  description?: string
}

export function ApiKeyRevealDialog({
  open,
  onOpenChange,
  apiKey,
  description = "Copy this key now. It won't be shown again, so store it somewhere safe.",
}: ApiKeyRevealDialogProps): React.JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API key created</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Secret</Label>
          <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
            <code className="flex-1 break-all font-mono text-xs">{apiKey?.api_key}</code>
            {apiKey && <CopyButton value={apiKey.api_key} label="Copy API key" />}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => { onOpenChange(false) }}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
